import { MDSListBox, MDSButton, MDSIconKind, MDSIcon, MDSIconColor } from "@/mds";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC, useCallback, useMemo } from "react";
import { useAppStore } from "@/store";
import { toastModule } from "@/modules/toast";
import { windowModule } from "@/modules/window";
import { APP_CONFIG } from "@/domains/global/config";
import {
  StyledContentListTextContainer,
  StyledContentWrapper,
} from "@/pages/integrations/shared/integrations.styles";
import { IntegrationsZapierPageDescription } from "@/pages/integrations/zapier/integrations-zapier-page-description";
import { useRefocus } from "@/domains/react/useRefocus";
import { IntegrationsLayoutBottomBuffer } from "@/pages/integrations/shared/integrations-layout";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { trackEvent } from "@/domains/metrics/segment";

export const IntegrationsZapierPageContent: FC = observer(() => {
  const { store } = useAppStore();
  const { isEnabled, disable, isDisabling } = store.zapierIntegration;
  const isConnected = isEnabled;

  useRefocus(
    () => {
      /**
       * Whenever we refocus this page, we reload the zapier integration settings.
       * (The zapier setup happens in a different tab, so we want to make sure we
       * reflect the latest status when they return to this page [without requiring
       * them to manually refresh].)
       */
      store.zapierIntegration.reload();
    },
    { immediate: true }
  );

  const handleDisconnectZapier = useCallback(async () => {
    try {
      await disable();
      trackEvent(TrackedEvent.ZapierDisconnect);

      toastModule.triggerToast({
        content: "Disconnected",
      });
    } catch (_unknownError) {
      toastModule.triggerToast({
        content: "Something went wrong. Please try again later.",
      });
    }
  }, [disable]);

  const handleClickZapierIntegrationLink = useCallback(() => {
    trackEvent(TrackedEvent.ZapierIntegrationLinkClick);
    windowModule.openInNewTab({ url: new URL(APP_CONFIG.URLS.ZAPIER_INTEGRATION) });
  }, []);

  const listItems = useMemo(() => {
    if (isEnabled) {
      return [
        {
          id: "1",
          children: (
            <StyledContentListTextContainer>
              <RowBoldText>Connected</RowBoldText>
              <MDSIcon kind={MDSIconKind.CheckCircleSolid} color={MDSIconColor.Positive} />
            </StyledContentListTextContainer>
          ),
          rightAddon: (
            <MDSButton isLoading={isDisabling} onClick={handleDisconnectZapier}>
              Disconnect
            </MDSButton>
          ),
        },
      ];
    }

    return [
      {
        id: "1",
        children: (
          <StyledContentListTextContainer>
            <RowBoldText>Not Connected</RowBoldText>
          </StyledContentListTextContainer>
        ),
        rightAddon: (
          <MDSButton onClick={handleClickZapierIntegrationLink}>Set up in Zapier</MDSButton>
        ),
      },
    ];
  }, [isEnabled, isDisabling, handleDisconnectZapier, handleClickZapierIntegrationLink]);

  return (
    <StyledContentWrapper>
      <IntegrationsZapierPageDescription
        isConnected={isConnected}
        onZapierIntegrationLinkClick={handleClickZapierIntegrationLink}
      />
      <MDSListBox items={listItems} />
      <IntegrationsLayoutBottomBuffer />
    </StyledContentWrapper>
  );
});

const RowBoldText = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;
